import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "@emotion/styled";
import { ViewportContext } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { getHistoryEntryPaidDate } from "@monorepo/helpers";
import { HistoryCalendarIcon } from "@monorepo/icons";
import { useGetCartGroupsWithTrashQuery } from "@monorepo/services/api/carts";
import { historyApi } from "@monorepo/services/api/history";
import theme, { iconsSizes } from "@monorepo/styles/theme";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton } from "@mui/material";
import { Box } from "@mui/system";
import { reverse } from "named-urls";

interface ICartSelectProps {
  cartGroupId: string;
}

interface IHistorySidebarHeaderProps {
  cartGroupId?: string;
}

interface ISArrow {
  isNext?: boolean;
  isBack?: boolean;
}

const SArrow = styled("div")<ISArrow>(({ isNext, isBack }) => ({
  width: 0,
  height: 0,
  borderTop: "0.4rem solid transparent",
  borderBottom: "0.4rem solid transparent",
  ...(isNext && {
    borderLeft: `0.4rem solid ${theme.palette.shuttleGray.main}`,
  }),
  ...(isBack && {
    borderRight: `0.4rem solid ${theme.palette.shuttleGray.main}`,
  }),
}));

const SIconButton = styled(IconButton)(() => ({
  width: "45px",
  height: "45px",
  padding: "1rem",
  cursor: "pointer",
  background: "none",
}));

const CartSelect: React.FC<ICartSelectProps> = ({ cartGroupId }) => {
  const history = useHistory();
  const { isMobile } = useContext(ViewportContext);
  const dispatch = useDispatch();
  const { data: cartGroups } = useGetCartGroupsWithTrashQuery();
  const { t } = useTranslation();

  const currentIndex = cartGroups?.findIndex((c) => String(c.id) === String(cartGroupId));
  const currentCart = currentIndex !== undefined && cartGroups ? cartGroups[currentIndex] : undefined;

  const handleChange = async (move: -1 | 1) => {
    if (!cartGroups || cartGroups.length <= 1 || currentIndex === undefined) return;

    let newIndex = currentIndex + move;

    if (newIndex < 0) newIndex = cartGroups.length - 1;
    else if (newIndex > cartGroups.length - 1) newIndex = 0;

    const cart = cartGroups[newIndex];
    const result = await dispatch(historyApi.endpoints.getHistoryBySearch.initiate());
    //todo dispatch type
    //@ts-ignore
    const firstEntry = result?.data?.find(
      (e) => String(e.cart_group_id) === String(cart.id) && typeof getHistoryEntryPaidDate(e) === "string"
    );

    if (firstEntry && !isMobile) {
      history.push(
        reverse(routeConfig.dashboard.payments.history.cartGroup.period.self, {
          cartGroupId: cart.id,
          period: getHistoryEntryPaidDate(firstEntry).slice(0, 7),
        })
      );
      return;
    }

    history.push(reverse(routeConfig.dashboard.payments.history.cartGroup.self, { cartGroupId: cart.id }));
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 2fr 1fr",
        gridGap: "2rem",
        placeItems: "center",
      }}
    >
      <SIconButton onClick={() => handleChange(-1)} aria-label={t("aria.back_cart")}>
        <SArrow isBack />
      </SIconButton>
      <Box
        sx={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          textAlign: "center",
          fontWeight: "bold",
          color: theme.palette.text.secondary,
          maxWidth: "180px",
          [theme.breakpoints.up("xl")]: {
            maxWidth: "200px",
          },
        }}
        title={currentCart?.name}
      >
        {currentCart?.name}
      </Box>
      <SIconButton onClick={() => handleChange(1)} aria-label={t("aria.next_cart")}>
        <SArrow isNext />
      </SIconButton>
    </Box>
  );
};

const IconWrapper: FC<{ onClick: () => void }> = ({ children, onClick }) => {
  return (
    <IconButton
      sx={{
        width: "45px",
        height: "45px",
        opacity: 0.35,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        stroke: theme.palette.shuttleGray.main,
        "&:hover": {
          opacity: 0.8,
        },
      }}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
};

export const HistorySidebarHeader: FC<IHistorySidebarHeaderProps> = ({ cartGroupId }) => {
  const history = useHistory();
  const { t } = useTranslation();

  if (!cartGroupId) return null;

  return (
    <Box display="grid" gridTemplateColumns="10fr 1fr 1fr" alignItems="center">
      <CartSelect cartGroupId={cartGroupId} />
      <IconWrapper
        onClick={() => history.push(reverse(routeConfig.dashboard.payments.history.cartGroup.filter, { cartGroupId }))}
        aria-label={t("aria.open_date_filters")}
      >
        <HistoryCalendarIcon
          fontSize="small"
          sx={{
            fontSize: iconsSizes.small,
            strokeWidth: "0.1px",
            stroke: theme.palette.primary.main,
          }}
        />
      </IconWrapper>
      <IconWrapper
        onClick={() => history.push(reverse(routeConfig.dashboard.payments.history.cartGroup.search, { cartGroupId }))}
        aria-label={t("aria.open_search")}
      >
        <SearchIcon
          sx={{
            fontSize: iconsSizes.medium,
            strokeWidth: "0.1px",
            fill: theme.palette.primary.main,
          }}
        />
      </IconWrapper>
    </Box>
  );
};
